import clsx from "clsx";
import React from "react";
import { FC } from "react";
import { PropTypes } from "@material-ui/core";
import { lighten } from "@material-ui/core";
import { Omit } from "@material-ui/types";
import { Theme } from "@material-ui/core/styles";
import { darken } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles";
import MuiButton from "@material-ui/core/Button";
import { ButtonProps as MuiButtonProps } from "@material-ui/core/Button";
import makeStyles from "@material-ui/core/styles/makeStyles";

const useStyles = makeStyles((theme: Theme) => ({
  btn: {
    color: "#231F20",
  },
  textSuccess: {
    color: theme.palette.success.main,
  },
  outlinedSuccess: {
    color: theme.palette.success.main,
    borderColor: theme.palette.success.main,
    "&:hover": {
      backgroundColor: fade(
        theme.palette.success.main,
        theme.palette.action.hoverOpacity,
      ),
    },
    "&:focus": {
      backgroundColor: fade(
        theme.palette.success.main,
        theme.palette.action.hoverOpacity,
      ),
    },
    "&:active": {
      backgroundColor: fade(
        theme.palette.success.main,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  containedSuccess: {
    color: "#ffffff!important",
    backgroundColor: theme.palette.success.main,
    "&:hover": {
      backgroundColor: darken(
        theme.palette.success.main,
        theme.palette.action.hoverOpacity,
      ),
      borderColor: darken(
        theme.palette.success.main,
        theme.palette.action.hoverOpacity,
      ),
    },
    "&:focus": {
      backgroundColor: darken(
        theme.palette.success.main,
        theme.palette.action.hoverOpacity,
      ),
      borderColor: darken(
        theme.palette.success.main,
        theme.palette.action.hoverOpacity,
      ),
    },
    "&:active": {
      backgroundColor: darken(
        theme.palette.success.main,
        theme.palette.action.hoverOpacity,
      ),
      borderColor: darken(
        theme.palette.success.main,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  textSecondaryDark: {
    color: theme.palette.secondary.dark,
  },
  outlinedSecondaryDark: {
    color: theme.palette.secondary.dark,
    borderColor: theme.palette.secondary.dark,
    "&:hover": {
      backgroundColor: fade(
        theme.palette.secondary.dark,
        theme.palette.action.hoverOpacity,
      ),
    },
    "&:focus": {
      backgroundColor: fade(
        theme.palette.secondary.dark,
        theme.palette.action.hoverOpacity,
      ),
    },
    "&:active": {
      backgroundColor: fade(
        theme.palette.secondary.dark,
        theme.palette.action.hoverOpacity,
      ),
    },
  },
  containedSecondaryDark: {
    color: "#ffffff!important",
    backgroundColor: theme.palette.secondary.dark,
    "&:hover": {
      backgroundColor: darken(
        theme.palette.secondary.dark,
        theme.palette.action.hoverOpacity,
      ),
      borderColor: darken(
        theme.palette.secondary.dark,
        theme.palette.action.hoverOpacity,
      ),
    },
    "&:focus": {
      backgroundColor: darken(
        theme.palette.secondary.dark,
        theme.palette.action.hoverOpacity,
      ),
      borderColor: darken(
        theme.palette.secondary.dark,
        theme.palette.action.hoverOpacity,
      ),
    },
    "&:active": {
      backgroundColor: darken(
        theme.palette.secondary.dark,
        theme.palette.action.hoverOpacity,
      ),
      borderColor: darken(
        theme.palette.secondary.dark,
        theme.palette.action.hoverOpacity,
      ),
    },
    "&:disabled": {
      pointerEvents: "auto",
      color: "#ffffff!important",
      backgroundColor: lighten(
        theme.palette.secondary.dark,
        theme.palette.action.hoverOpacity,
      ),
      cursor: "not-allowed",
      "&:hover": {
        backgroundColor: lighten(
          theme.palette.secondary.dark,
          theme.palette.action.hoverOpacity,
        ),
      },
    },
  },
}));

const Button: FC<ButtonProps> = ({ color, className, ...rest }) => {
  const classes: any = useStyles();
  switch (color) {
    case "success": {
      return (
        <MuiButton
          color="default"
          className={clsx(
            classes.btn,
            className,
            rest && rest.variant && classes[`${rest.variant}Success`],
          )}
          {...rest}
        />
      );
    }
    case "secondary-dark": {
      return (
        <MuiButton
          color="default"
          className={clsx(
            classes.btn,
            className,
            rest && rest.variant && classes[`${rest.variant}SecondaryDark`],
          )}
          {...rest}
        />
      );
    }
    case "danger": {
      return (
        <MuiButton
          color="default"
          className={clsx(
            classes.btn,
            className,
            rest && rest.variant && classes[`${rest.variant}Danger`],
          )}
          {...rest}
        />
      );
    }
    default:
      return (
        <MuiButton
          {...rest}
          color={color}
          className={clsx(className, classes.btn)}
        />
      );
  }
};

export default React.memo(Button);

type CustomColor = "danger" | "success" | "secondary-dark";
type ButtonColor = CustomColor | PropTypes.Color;

export interface ButtonProps extends Omit<MuiButtonProps, "color"> {
  color?: ButtonColor;
  component?: any;
  to?: any;
  target?: any;
}
